import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { LanguageSelector } from "@/components/ui/language-selector";
import { ArrowLeft, UserPlus, Save } from "lucide-react";

const translations = {
  en: {
    title: "Add New Client",
    subtitle: "Add a new buyer, seller, or broker to your client list",
    back: "Back to Dashboard",
    basicInfo: "Basic Information",
    name: "Full Name",
    namePlaceholder: "Enter client's full name",
    email: "Email Address",
    emailPlaceholder: "client@example.com",
    phone: "Phone Number",
    phonePlaceholder: "+1 (555) 000-0000",
    company: "Company Name",
    companyPlaceholder: "Company or organization",
    clientType: "Client Type",
    selectType: "Select client type",
    buyer: "Buyer",
    seller: "Seller",
    broker: "Broker",
    additionalInfo: "Additional Information",
    notes: "Notes",
    notesPlaceholder: "Add any notes about this client...",
    profileUrl: "Profile URL (optional)",
    profileUrlPlaceholder: "https://linkedin.com/in/...",
    save: "Add Client",
    saving: "Adding...",
    success: "Client Added",
    successMessage: "Client has been added successfully",
    error: "Error",
    errorMessage: "Failed to add client",
    required: "This field is required",
  },
  fr: {
    title: "Ajouter un Nouveau Client",
    subtitle: "Ajoutez un nouveau acheteur, vendeur ou courtier à votre liste de clients",
    back: "Retour au Tableau de Bord",
    basicInfo: "Informations de Base",
    name: "Nom Complet",
    namePlaceholder: "Entrez le nom complet du client",
    email: "Adresse Email",
    emailPlaceholder: "client@exemple.com",
    phone: "Numéro de Téléphone",
    phonePlaceholder: "+1 (555) 000-0000",
    company: "Nom de l'Entreprise",
    companyPlaceholder: "Entreprise ou organisation",
    clientType: "Type de Client",
    selectType: "Sélectionnez le type de client",
    buyer: "Acheteur",
    seller: "Vendeur",
    broker: "Courtier",
    additionalInfo: "Informations Supplémentaires",
    notes: "Notes",
    notesPlaceholder: "Ajoutez des notes sur ce client...",
    profileUrl: "URL du Profil (optionnel)",
    profileUrlPlaceholder: "https://linkedin.com/in/...",
    save: "Ajouter Client",
    saving: "Ajout en cours...",
    success: "Client Ajouté",
    successMessage: "Le client a été ajouté avec succès",
    error: "Erreur",
    errorMessage: "Échec de l'ajout du client",
    required: "Ce champ est requis",
  },
};

export default function AddClient() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const t = translations[language as keyof typeof translations] || translations.en;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    type: "" as 'buyer' | 'seller' | 'broker' | '',
    notes: "",
    profileUrl: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) newErrors.name = t.required;
    if (!formData.email.trim()) newErrors.email = t.required;
    if (!formData.phone.trim()) newErrors.phone = t.required;
    if (!formData.type) newErrors.type = t.required;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !user) return;
    
    setIsSubmitting(true);
    
    try {
      await addDoc(collection(db, "consultantClients"), {
        consultantId: user.email,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        type: formData.type,
        notes: formData.notes,
        profileUrl: formData.profileUrl,
        status: 'active',
        activeMandates: 0,
        totalPaid: 0,
        addedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      
      toast({
        title: t.success,
        description: t.successMessage,
      });
      
      navigate("/dashboard/consultant");
    } catch (error) {
      console.error("Error adding client:", error);
      toast({
        title: t.error,
        description: t.errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="h-16 border-b border-border bg-card px-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard/consultant")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t.back}
          </Button>
        </div>
        <LanguageSelector />
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <UserPlus className="h-8 w-8 text-primary" />
            {t.title}
          </h1>
          <p className="text-muted-foreground mt-2">{t.subtitle}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>{t.basicInfo}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">{t.name} *</Label>
                  <Input
                    id="name"
                    placeholder={t.namePlaceholder}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={errors.name ? "border-destructive" : ""}
                  />
                  {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type">{t.clientType} *</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value: 'buyer' | 'seller' | 'broker') =>
                      setFormData({ ...formData, type: value })
                    }
                  >
                    <SelectTrigger className={errors.type ? "border-destructive" : ""}>
                      <SelectValue placeholder={t.selectType} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="buyer">{t.buyer}</SelectItem>
                      <SelectItem value="seller">{t.seller}</SelectItem>
                      <SelectItem value="broker">{t.broker}</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.type && <p className="text-sm text-destructive">{errors.type}</p>}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="email">{t.email} *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t.emailPlaceholder}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={errors.email ? "border-destructive" : ""}
                  />
                  {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{t.phone} *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder={t.phonePlaceholder}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className={errors.phone ? "border-destructive" : ""}
                  />
                  {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company">{t.company}</Label>
                <Input
                  id="company"
                  placeholder={t.companyPlaceholder}
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Additional Information */}
          <Card>
            <CardHeader>
              <CardTitle>{t.additionalInfo}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="profileUrl">{t.profileUrl}</Label>
                <Input
                  id="profileUrl"
                  placeholder={t.profileUrlPlaceholder}
                  value={formData.profileUrl}
                  onChange={(e) => setFormData({ ...formData, profileUrl: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">{t.notes}</Label>
                <Textarea
                  id="notes"
                  placeholder={t.notesPlaceholder}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/dashboard/consultant")}
            >
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              <Save className="mr-2 h-4 w-4" />
              {isSubmitting ? t.saving : t.save}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
